import { JobFunction } from '@atlas/types'
import type { WorkOrder } from '@/domain/work-order.types'

/** Who holds an order on its step. */
export enum StageTurn {
	PM = 'pm',
	DESIGNER = 'designer',
}

/** The status every order starts in, where its PM confirms the request is complete. */
export const CREATED_STATUS = 'created'

/** Where the PM completes the order's details and sends the task to the designer. */
export const DETAILS_STATUS = 'sources_verified'

/** The steps whose screen is built; every other open status shows a placeholder. */
const BUILT_STEPS: ReadonlySet<string> = new Set([
	CREATED_STATUS,
	'backlog',
	'review_source_material',
	'pending_source_material',
	DETAILS_STATUS,
])

/** Whether the status has its own screen rather than the placeholder. */
export const hasBuiltScreen = (status: string): boolean => BUILT_STEPS.has(status)

/**
 * Whose move it is, by status. Read off the order, not a role: there is no PM or
 * designer role, so the PM is the order's `pmUserId` and the designer one of its
 * assignees. A terminal status has no turn.
 */
const TURN_BY_STATUS: Readonly<Record<string, StageTurn>> = {
	[CREATED_STATUS]: StageTurn.PM,
	backlog: StageTurn.PM,
	review_source_material: StageTurn.DESIGNER,
	pending_source_material: StageTurn.PM,
	sources_verified: StageTurn.PM,
	ready_to_design: StageTurn.DESIGNER,
	in_progress: StageTurn.DESIGNER,
	internal_review: StageTurn.PM,
	external_review: StageTurn.PM,
	changes_requested: StageTurn.DESIGNER,
	upload_distribution_package: StageTurn.PM,
}

/** The turn of the current status, or null when the screen offers no action in it. */
export const turnOf = (status: string): StageTurn | null => TURN_BY_STATUS[status] ?? null

/** The signed-in user, as far as the turn cares. */
export interface TurnUser {
	userId: string
	jobFunction?: string
}

/** Whether the user may pick the order's PM: a PM, while the order is on Created and theirs or nobody's. */
export const mayPickPm = (order: Pick<WorkOrder, 'status' | 'pmUserId'>, user: TurnUser | null | undefined): boolean =>
	!!user &&
	order.status === CREATED_STATUS &&
	(order.pmUserId === user.userId || (!order.pmUserId && user.jobFunction === JobFunction.PM))

/** Whether the signed-in user holds the order's current turn. */
export function holdsTurn(
	order: Pick<WorkOrder, 'status' | 'pmUserId' | 'assigneeIds'>,
	user: TurnUser | null | undefined,
): boolean {
	if (!user) return false
	switch (turnOf(order.status)) {
		case StageTurn.PM:
			return order.pmUserId === user.userId
		case StageTurn.DESIGNER:
			return order.assigneeIds.includes(user.userId)
		default:
			return false
	}
}
